import { ArrowLeft, Plus, RefreshCw, Share2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  type CustomerProfile,
  type CustomerShare,
  useCreateCustomerMutation,
  useGetCustomerQuery,
  useGetCustomersQuery,
  useGetCustomerSharesQuery,
  useGrantCustomerShareMutation,
  useRevokeCustomerShareMutation,
  useUpdateCustomerMutation,
} from '@/store/api/customersApi';

const CUSTOMER_SCOPES = [
  'basic_profile',
  'contact_info',
  'purchase_history',
  'loyalty_points',
  'wallet_balance',
  'membership_status',
] as const;

export function CustomersPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const isCreate = customerId === 'new';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<CustomerProfile[]>([]);
  const {
    data: page,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetCustomersQuery(
    { cursor, limit: 25, search: search.trim() || undefined, status: status.trim() || undefined },
    { skip: Boolean(customerId) },
  );
  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isCustomerError,
    refetch: refetchCustomer,
  } = useGetCustomerQuery(customerId ?? '', { skip: !customerId || isCreate });
  const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: isSaving }] = useUpdateCustomerMutation();
  const [edit, setEdit] = useState(false);
  const [formName, setFormName] = useState('');

  useEffect(() => {
    setRows([]);
    setCursor(undefined);
  }, [search, status]);
  useEffect(() => {
    if (!page) {
      return;
    }
    setRows((previous) => {
      if (!cursor) {
        return page.data;
      }
      const seen = new Set(previous.map((row) => row.id));
      return [...previous, ...page.data.filter((row) => !seen.has(row.id))];
    });
  }, [page, cursor]);
  useEffect(() => {
    setFormName(customer?.displayName ?? '');
  }, [customer]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!formName.trim()) {
      return;
    }
    try {
      if (isCreate) {
        const created = await createCustomer({
          displayName: formName.trim(),
          createdBy: 'user',
        }).unwrap();
        toast.success(`Customer “${created.displayName ?? created.id}” created`);
        navigate(`/customers/${created.id}`);
      } else if (customerId) {
        const updated = await updateCustomer({
          id: customerId,
          body: { displayName: formName.trim() },
        }).unwrap();
        toast.success(`Customer “${updated.displayName ?? updated.id}” saved`);
        setEdit(false);
      }
    } catch {
      toast.error(
        isCreate
          ? 'Could not create the customer. Your entry is still here; try again.'
          : 'Could not save the customer. Your changes are still here; try again.',
      );
    }
  }

  if (isCreate || customerId) {
    if (isCreate) {
      return (
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => navigate('/customers')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Customers
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Create customer</h1>
            <p className="text-sm text-muted-foreground">
              Create a customer profile in the authenticated organization.
            </p>
          </div>
          <Card>
            <CardContent className="pt-6">
              <form
                className="max-w-xl space-y-4"
                onSubmit={(event) => {
                  void save(event);
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="customer-name">Display name</Label>
                  <Input
                    id="customer-name"
                    required
                    autoFocus
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => navigate('/customers')}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? 'Creating…' : 'Create customer'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <CustomerDetail
        customerId={customerId}
        customer={customer}
        isLoading={isLoadingCustomer}
        isError={isCustomerError}
        retry={() => {
          void refetchCustomer();
        }}
        edit={edit}
        setEdit={setEdit}
        formName={formName}
        setFormName={setFormName}
        save={(event) => {
          void save(event);
        }}
        isSaving={isSaving}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-sm text-muted-foreground">
            Organization-scoped profiles. Merged profiles are excluded by default.
          </p>
        </div>
        <Button asChild>
          <Link to="/customers/new">
            <Plus className="mr-2 h-4 w-4" /> Create customer
          </Link>
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="min-w-56 flex-1">
          <Label htmlFor="customer-search">Search customers</Label>
          <Input
            id="customer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Display name"
          />
        </div>
        <div className="w-48">
          <Label htmlFor="customer-status">Status</Label>
          <Input
            id="customer-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Any status"
          />
        </div>
      </div>
      <section aria-busy={isLoading || isFetching}>
        {isLoading && rows.length === 0 ? (
          <LoadingRows label="Loading customers" columns={5} />
        ) : isError && rows.length === 0 ? (
          <Retry
            onRetry={() => {
              void refetch();
            }}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={search || status ? 'No matching customers' : 'No customers yet'}
            description="Customer profiles appear here when created in this organization."
            action={
              search || status ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('');
                    setStatus('');
                  }}
                >
                  Clear filters
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/customers/new">Create customer</Link>
                </Button>
              )
            }
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto" role="region" aria-label="Customers table">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      {['Customer', 'Profile ID', 'Status', 'Created', 'Updated'].map((label) => (
                        <th key={label} className="px-4 py-3 font-medium">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">
                          <Link
                            className="text-primary hover:underline"
                            to={`/customers/${row.id}`}
                          >
                            {row.displayName ?? 'Unnamed customer'}
                          </Link>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{row.id}</td>
                        <td className="px-4 py-3">{row.status}</td>
                        <td className="px-4 py-3">
                          {new Date(row.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          {new Date(row.updatedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
        {isError && rows.length > 0 && (
          <Retry
            onRetry={() => {
              void refetch();
            }}
          />
        )}
        {page?.pagination.hasMore && (
          <div className="mt-3 flex justify-center">
            <Button
              variant="outline"
              disabled={isFetching}
              onClick={() => setCursor(page.pagination.cursor ?? undefined)}
            >
              {isFetching ? 'Loading…' : 'Load more customers'}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

function CustomerDetail(props: {
  customerId: string;
  customer?: CustomerProfile;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
  edit: boolean;
  setEdit: (value: boolean) => void;
  formName: string;
  setFormName: (value: string) => void;
  save: (event: FormEvent<HTMLFormElement>) => void;
  isSaving: boolean;
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'shares'>('profile');
  const [status, setStatus] = useState('');
  const [granteeOrgId, setGranteeOrgId] = useState('');
  const [shareCursor, setShareCursor] = useState<string | undefined>();
  const [shares, setShares] = useState<CustomerShare[]>([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [scopeSelection, setScopeSelection] = useState<string[]>(['basic_profile']);
  const [expiresAt, setExpiresAt] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<CustomerShare | null>(null);
  const toast = useToast();
  const {
    data: sharePage,
    isLoading: isLoadingShares,
    isFetching,
    isError,
    refetch,
  } = useGetCustomerSharesQuery({
    id: props.customerId,
    cursor: shareCursor,
    limit: 25,
    status: status || undefined,
    granteeOrgId: granteeOrgId.trim() || undefined,
  });
  const [grantShare, { isLoading: isGranting }] = useGrantCustomerShareMutation();
  const [revokeShare, { isLoading: isRevoking }] = useRevokeCustomerShareMutation();

  useEffect(() => {
    setShares([]);
    setShareCursor(undefined);
  }, [status, granteeOrgId, props.customerId]);
  useEffect(() => {
    if (!sharePage) {
      return;
    }
    setShares((previous) => {
      if (!shareCursor) {
        return sharePage.data;
      }
      const seen = new Set(previous.map((share) => share.id));
      return [...previous, ...sharePage.data.filter((share) => !seen.has(share.id))];
    });
  }, [sharePage, shareCursor]);

  async function submitGrant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!granteeOrgId.trim() || !scopeSelection.length) {
      return;
    }
    try {
      await grantShare({
        id: props.customerId,
        granteeOrgId: granteeOrgId.trim(),
        scopes: scopeSelection,
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      }).unwrap();
      toast.success('Customer share granted');
      setGrantOpen(false);
      setExpiresAt('');
    } catch {
      toast.error(
        'Could not grant access. Verify the organization and selected scopes, then try again.',
      );
    }
  }

  async function confirmRevoke() {
    if (!revokeTarget) {
      return;
    }
    try {
      await revokeShare({ id: props.customerId, policyId: revokeTarget.id }).unwrap();
      toast.success('Customer share revoked');
    } catch {
      toast.error('Could not revoke this share. Try again.');
    } finally {
      setRevokeTarget(null);
    }
  }

  if (props.isLoading) {
    return <LoadingSpinner className="py-12" />;
  }
  if (props.isError || !props.customer) {
    return (
      <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
        Could not load this customer profile.{' '}
        <Button variant="link" onClick={props.retry}>
          <RefreshCw className="mr-1 h-3 w-3" /> Try again
        </Button>
      </div>
    );
  }
  const customer = props.customer;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/customers')}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Customers
      </Button>
      <div>
        <h1 className="text-2xl font-bold">{customer.displayName ?? 'Unnamed customer'}</h1>
        <p className="text-sm text-muted-foreground">Customer profile · {customer.status}</p>
      </div>
      <div className="flex gap-2 border-b" role="group" aria-label="Customer sections">
        <button
          type="button"
          aria-pressed={activeTab === 'profile'}
          className={`border-b-2 px-3 py-2 text-sm ${activeTab === 'profile' ? 'border-primary font-medium' : 'border-transparent text-muted-foreground'}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          type="button"
          aria-pressed={activeTab === 'shares'}
          className={`border-b-2 px-3 py-2 text-sm ${activeTab === 'shares' ? 'border-primary font-medium' : 'border-transparent text-muted-foreground'}`}
          onClick={() => setActiveTab('shares')}
        >
          <Share2 className="mr-1 inline h-4 w-4" /> Shares
        </button>
      </div>
      {activeTab === 'profile' ? (
        props.edit ? (
          <Card>
            <CardContent className="pt-6">
              <form
                className="max-w-xl space-y-4"
                onSubmit={(event) => {
                  void props.save(event);
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="customer-display-name">Display name</Label>
                  <Input
                    id="customer-display-name"
                    required
                    value={props.formName}
                    onChange={(e) => props.setFormName(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Only the display name is editable in the current customer contract.
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" type="button" onClick={() => props.setEdit(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={props.isSaving}>
                    {props.isSaving ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <dl className="grid gap-4 sm:grid-cols-2">
                <Detail label="Profile ID" value={customer.id} mono />
                <Detail label="Status" value={customer.status} />
                <Detail label="Created by" value={customer.createdBy} />
                <Detail label="Created" value={new Date(customer.createdAt).toLocaleString()} />
                <Detail label="Updated" value={new Date(customer.updatedAt).toLocaleString()} />
              </dl>
              <div className="flex justify-end border-t pt-4">
                <Button variant="outline" onClick={() => props.setEdit(true)}>
                  Edit profile
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      ) : (
        <section className="space-y-4" aria-busy={isLoadingShares || isFetching}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="grid flex-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="share-status">Status</Label>
                <select
                  id="share-status"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="revoked">Revoked</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <Label htmlFor="share-grantee-filter">Recipient organization ID</Label>
                <Input
                  id="share-grantee-filter"
                  value={granteeOrgId}
                  onChange={(e) => setGranteeOrgId(e.target.value)}
                />
              </div>
            </div>
            <Button onClick={() => setGrantOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Grant access
            </Button>
          </div>
          {isLoadingShares && shares.length === 0 ? (
            <LoadingRows label="Loading customer shares" columns={6} />
          ) : isError && shares.length === 0 ? (
            <Retry
              onRetry={() => {
                void refetch();
              }}
            />
          ) : shares.length === 0 ? (
            <EmptyState
              title="No shares found"
              description="No customer share records match this selection."
              action={
                status || granteeOrgId ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStatus('');
                      setGranteeOrgId('');
                    }}
                  >
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto" role="region" aria-label="Customer shares table">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b bg-muted/40">
                      <tr>
                        {['Recipient organization', 'Scopes', 'Status', 'Expiry', 'Granted'].map(
                          (label) => (
                            <th key={label} className="px-4 py-3 font-medium">
                              {label}
                            </th>
                          ),
                        )}
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {shares.map((share) => {
                        const shareStatus = share.revokedAt
                          ? 'revoked'
                          : share.expiresAt && new Date(share.expiresAt) <= new Date()
                            ? 'expired'
                            : 'active';
                        return (
                          <tr key={share.id} className="border-b last:border-0">
                            <td className="px-4 py-3 font-mono text-xs">{share.granteeOrgId}</td>
                            <td className="max-w-sm px-4 py-3">{share.scopes.join(', ')}</td>
                            <td className="px-4 py-3">{shareStatus}</td>
                            <td className="px-4 py-3">
                              {share.expiresAt
                                ? new Date(share.expiresAt).toLocaleString()
                                : 'No expiry'}
                            </td>
                            <td className="px-4 py-3">
                              {new Date(share.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              {shareStatus === 'active' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setRevokeTarget(share)}
                                >
                                  Revoke
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          {isError && shares.length > 0 && (
            <Retry
              onRetry={() => {
                void refetch();
              }}
            />
          )}
          {sharePage?.pagination.hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                disabled={isFetching}
                onClick={() => setShareCursor(sharePage.pagination.cursor ?? undefined)}
              >
                {isFetching ? 'Loading…' : 'Load more shares'}
              </Button>
            </div>
          )}
        </section>
      )}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant customer access</DialogTitle>
            <DialogDescription>
              Share the selected customer scopes with another organization.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              void submitGrant(event);
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="grantee-org">Recipient organization ID</Label>
              <Input
                id="grantee-org"
                required
                value={granteeOrgId}
                onChange={(e) => setGranteeOrgId(e.target.value)}
              />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Access scopes</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {CUSTOMER_SCOPES.map((scope) => (
                  <label key={scope} className="flex min-h-10 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={scopeSelection.includes(scope)}
                      onChange={(event) =>
                        setScopeSelection((current) =>
                          event.target.checked
                            ? [...current, scope]
                            : current.filter((item) => item !== scope),
                        )
                      }
                    />
                    {scope.replaceAll('_', ' ')}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="space-y-2">
              <Label htmlFor="share-expiry">Expiry (optional)</Label>
              <Input
                id="share-expiry"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGrantOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isGranting || scopeSelection.length === 0}>
                {isGranting ? 'Granting…' : 'Grant access'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title="Revoke customer access"
        description={`Revoke access for organization ${revokeTarget?.granteeOrgId ?? ''}? This removes the selected scopes for this customer profile.`}
        confirmLabel="Revoke access"
        variant="destructive"
        onConfirm={() => {
          void confirmRevoke();
        }}
        isLoading={isRevoking}
      />
    </div>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? 'break-all font-mono' : ''}`}>{value || '—'}</dd>
    </div>
  );
}

function Retry({ onRetry }: { onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
      Could not load customer data.{' '}
      <Button variant="link" onClick={onRetry}>
        <RefreshCw className="mr-1 h-3 w-3" /> Try again
      </Button>
    </div>
  );
}
